import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon, DocumentMagnifyingGlassIcon, ExclamationTriangleIcon, MagnifyingGlassIcon,
  PaperAirplaneIcon, PlusIcon, TrashIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  chatFragen, chatLesen, chatLoeschen, chatNeu, chatsListe,
  type ChatKurz, type ChatNachricht, type ChatQuelle,
} from '../api/client';
import Antwort from '../components/chat/Antwort';
import DateiVorschau from '../components/datei/DateiVorschau';
import { useDialog } from '../contexts/ConfirmContext';
import { datumCH } from '../utils/laufwerk';

/**
 * Fragen (M3): Chat über die Dateien des Laufwerks — wie der Chat in PowerCare, nur über Firmendateien. Die KI sucht
 * selbst (mit den Rechten des Nutzers), fasst Angaben aus mehreren Dateien zusammen und belegt sie mit Quellen [n];
 * ein Klick auf eine Quelle öffnet sie rechts (Original vom Laufwerk oder Text). Gespräche sind privat und werden
 * nach der Aufbewahrungsfrist gelöscht (A18).
 */

const BEISPIELE = [
  'Welche Kündigungsfrist steht in unseren Mietverträgen?',
  'Was haben wir mit Swisscom vereinbart?',
  'Wer ist bei uns für die Informatik zuständig?',
];

type Laufend = { status: string[]; text: string };

export default function Fragen() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { confirm } = useDialog();
  const [chats, setChats] = useState<ChatKurz[]>([]);
  const [nachrichten, setNachrichten] = useState<ChatNachricht[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [laufend, setLaufend] = useState<Laufend | null>(null);
  const [fehler, setFehler] = useState<{ text: string; frage: string } | null>(null);
  const [eingabe, setEingabe] = useState('');
  const [quelle, setQuelle] = useState<{ q: ChatQuelle; begriffe: string } | null>(null);
  const [listeOffen, setListeOffen] = useState(false);
  const unten = useRef<HTMLDivElement>(null);
  const eben = useRef<string | null>(null);          // gerade angelegtes Gespräch: nicht neu laden (Antwort läuft)
  const gestartet = useRef(false);

  const listeLaden = useCallback(() => { chatsListe().then(setChats).catch(() => {}); }, []);
  useEffect(() => { listeLaden(); }, [listeLaden]);

  useEffect(() => {
    setQuelle(null); setFehler(null);
    if (!id) { setNachrichten([]); return; }
    if (eben.current === id) { eben.current = null; return; }
    let weg = false;
    setLaedt(true);
    chatLesen(id)
      .then((c) => { if (!weg) setNachrichten(c.nachrichten); })
      .catch(() => { if (!weg) navigate('/fragen', { replace: true }); })
      .finally(() => { if (!weg) setLaedt(false); });
    return () => { weg = true; };
  }, [id, navigate]);

  useEffect(() => { unten.current?.scrollIntoView({ block: 'end' }); }, [nachrichten, laufend, fehler]);

  const senden = useCallback(async (frage: string, nochmals = false) => {
    frage = frage.trim();
    if (!frage || laufend) return;
    setFehler(null); setEingabe('');
    let cid = id;
    try {
      if (!cid) {
        cid = (await chatNeu()).id;
        eben.current = cid;
        navigate(`/fragen/${cid}`);
      }
    } catch (e: any) { setFehler({ text: e?.message ?? String(e), frage }); return; }
    if (!nochmals) {
      setNachrichten((n) => [...n, { id: `neu-${Date.now()}`, rolle: 'user', inhalt: frage, angelegt: new Date().toISOString(), quellen: [] }]);
    }
    setLaufend({ status: [], text: '' });
    try {
      await chatFragen(cid!, frage, (e) => {
        if (e.typ === 'status') setLaufend((l) => l && { ...l, status: [...l.status, e.text] });
        else if (e.typ === 'text') setLaufend((l) => l && { ...l, text: l.text + e.text });
        else if (e.typ === 'verwerfen') setLaufend((l) => l && { ...l, text: '' });
        else if (e.typ === 'fertig') {
          setNachrichten((n) => [...n, { id: e.id, rolle: 'assistant', inhalt: e.text, angelegt: new Date().toISOString(), quellen: e.quellen, suchen: e.suchen }]);
          setLaufend(null);
        } else if (e.typ === 'fehler') { setFehler({ text: e.text, frage }); setLaufend(null); }
      });
    } catch (e: any) {
      setFehler({ text: e?.message ?? String(e), frage });
    } finally {
      setLaufend(null);
      listeLaden();
    }
  }, [id, laufend, navigate, listeLaden]);

  // Von der Startseite: /fragen?frage=… → gleich stellen
  useEffect(() => {
    const f = sp.get('frage');
    if (f && !id && !gestartet.current) {
      gestartet.current = true;
      setSp({}, { replace: true });
      senden(f);
    }
  }, [sp, id, setSp, senden]);

  const loeschen = async (c: ChatKurz) => {
    if (!await confirm({ title: 'Gespräch löschen?', message: `«${c.titel}» wird endgültig gelöscht.`, okLabel: 'Löschen', danger: true })) return;
    await chatLoeschen(c.id).catch(() => {});
    if (c.id === id) navigate('/fragen');
    listeLaden();
  };

  const absenden = (e: React.FormEvent) => { e.preventDefault(); senden(eingabe); };
  const taste = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); senden(eingabe); }
  };
  const quelleOeffnen = (m: ChatNachricht) => (q: ChatQuelle) => setQuelle({ q, begriffe: (m.suchen ?? []).join(' ') });

  const liste = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 p-3">
        <button onClick={() => { setListeOffen(false); navigate('/fragen'); }} className="btn-primary flex flex-1 items-center justify-center gap-1.5 text-sm">
          <PlusIcon className="h-4 w-4" /> Neues Gespräch
        </button>
        <button onClick={() => setListeOffen(false)} className="rounded-lg p-2 text-ps-dark-400 hover:bg-gray-100 lg:hidden" aria-label="Liste schliessen">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto p-2">
        {chats.length === 0 && <li className="px-2 py-3 text-xs text-ps-dark-400">Noch keine Gespräche.</li>}
        {chats.map((c) => (
          <li key={c.id} className="group relative">
            <button onClick={() => { setListeOffen(false); navigate(`/fragen/${c.id}`); }}
              className={`w-full rounded-md px-2.5 py-2 pr-8 text-left ${c.id === id ? 'bg-primary-50 text-primary-900' : 'text-ps-dark-700 hover:bg-gray-50'}`}>
              <div className="truncate text-[13px] font-medium">{c.titel}</div>
              <div className="text-[11px] text-ps-dark-400">{datumCH(c.geaendert)}</div>
            </button>
            <button onClick={() => loeschen(c)} aria-label="Gespräch löschen"
              className="absolute right-1.5 top-2 rounded p-1 text-ps-dark-300 opacity-0 hover:bg-rose-50 hover:text-rose-600 focus:opacity-100 group-hover:opacity-100">
              <TrashIcon className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      <div className="border-t border-gray-100 px-3 py-2 text-[11px] leading-snug text-ps-dark-400">
        Gespräche sehen nur Sie. Sie werden 90 Tage nach der letzten Frage gelöscht.
      </div>
    </div>
  );

  return (
    <div className="mx-auto flex h-[calc(100dvh-7rem)] max-w-[1800px] gap-4">
      {/* Gesprächsliste: Desktop links, Handy als Schublade */}
      <aside className={`hidden w-64 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm ${quelle ? '2xl:block' : 'lg:block'}`}>{liste}</aside>
      {listeOffen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="w-72 max-w-[85vw] bg-white shadow-xl">{liste}</div>
          <button className="flex-1 bg-black/30" aria-label="Schliessen" onClick={() => setListeOffen(false)} />
        </div>
      )}

      {/* Verlauf */}
      <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 lg:hidden">
          <button onClick={() => setListeOffen(true)} className="btn-ghost flex items-center gap-1.5 text-sm">
            <ChatBubbleLeftRightIcon className="h-4 w-4" /> Gespräche
          </button>
          <div className="flex-1" />
          {id && <button onClick={() => navigate('/fragen')} className="btn-ghost flex items-center gap-1 text-sm"><PlusIcon className="h-4 w-4" /> Neu</button>}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          {!id && nachrichten.length === 0 && !laufend && (
            <div className="mx-auto mt-6 max-w-xl text-center sm:mt-16">
              <DocumentMagnifyingGlassIcon className="mx-auto h-10 w-10 text-primary-500" />
              <h1 className="mt-3 text-xl font-semibold text-ps-dark-900">Was möchten Sie wissen?</h1>
              <p className="mt-1 text-sm text-ps-dark-500">Ich suche in Ihren Dateien, fasse zusammen und nenne zu jeder Angabe die Quelle.</p>
              <div className="mt-5 flex flex-col gap-2">
                {BEISPIELE.map((b) => (
                  <button key={b} onClick={() => senden(b)} className="rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-ps-dark-700 hover:border-primary-300 hover:bg-primary-50">{b}</button>
                ))}
              </div>
            </div>
          )}
          {laedt && <div className="space-y-2"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>}

          <div className="mx-auto max-w-3xl space-y-5">
            {nachrichten.map((m) => m.rolle === 'user' ? (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary-500 px-4 py-2 text-[14.5px] text-white">{m.inhalt}</div>
              </div>
            ) : (
              <div key={m.id} className="flex gap-3">
                <DocumentMagnifyingGlassIcon className="mt-0.5 h-6 w-6 shrink-0 text-primary-500" />
                <div className="min-w-0 flex-1">
                  <Antwort text={m.inhalt} quellen={m.quellen} onQuelle={quelleOeffnen(m)} aktiv={quelle?.q.datei_id} />
                </div>
              </div>
            ))}

            {laufend && (
              <div className="flex gap-3">
                <DocumentMagnifyingGlassIcon className="mt-0.5 h-6 w-6 shrink-0 animate-pulse text-primary-500" />
                <div className="min-w-0 flex-1">
                  <ul className="space-y-0.5">
                    {laufend.status.map((s, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-[12.5px] text-ps-dark-500">
                        <MagnifyingGlassIcon className="h-3.5 w-3.5" /> {s}
                      </li>
                    ))}
                    {!laufend.text && <li className="text-[12.5px] text-ps-dark-400">{laufend.status.length ? 'Lese die Fundstellen …' : 'Denke nach …'}</li>}
                  </ul>
                  {laufend.text && <div className="mt-2"><Antwort text={laufend.text} quellen={[]} onQuelle={() => {}} /></div>}
                </div>
              </div>
            )}

            {fehler && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="flex-1">{fehler.text}</div>
                <button onClick={() => senden(fehler.frage, true)} className="font-medium underline">Nochmals versuchen</button>
              </div>
            )}
            <div ref={unten} />
          </div>
        </div>

        <form onSubmit={absenden} className="border-t border-gray-200 p-3 sm:p-4">
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <textarea value={eingabe} onChange={(e) => setEingabe(e.target.value)} onKeyDown={taste} rows={Math.min(6, Math.max(1, eingabe.split('\n').length))}
              placeholder="Frage zu Ihren Dateien stellen …" aria-label="Frage" autoFocus
              className="input-field min-h-[42px] resize-none py-2.5" />
            <button type="submit" disabled={!eingabe.trim() || !!laufend} className="btn-primary flex h-[42px] items-center gap-1.5" aria-label="Senden">
              <PaperAirplaneIcon className="h-4 w-4" /><span className="hidden sm:inline">Senden</span>
            </button>
          </div>
          <p className="mx-auto mt-1.5 max-w-3xl text-[11px] text-ps-dark-400">Antworten stammen nur aus Ihren Dateien. Wichtige Angaben bitte in der Quelle prüfen.</p>
        </form>
      </section>

      {/* Quelle */}
      {quelle && quelle.q.datei_id && (
        <div className="fixed inset-0 z-50 bg-white lg:static lg:z-auto lg:w-[42%] lg:shrink-0 lg:bg-transparent">
          <DateiVorschau key={`${quelle.q.datei_id}#${quelle.q.abschnitt ?? ''}`} id={quelle.q.datei_id} q={quelle.begriffe}
            um={quelle.q.abschnitt ?? null} onClose={() => setQuelle(null)} />
        </div>
      )}
    </div>
  );
}
